import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { EASE } from "./Reveal";
import { useSectionNav } from "@/lib/useSectionNav";

const NAV_LINKS = [
  { label: "Our Philosophy", type: "section", target: "philosophy" },
  { label: "Why Gaia Space", type: "section", target: "about" },
  { label: "Gatherings", type: "route", to: "/gatherings" },
  { label: "Newsletter", type: "section", target: "newsletter" },
  { label: "Contact", type: "section", target: "contact" },
];

const Navigation = ({ solid = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const goToSection = useSectionNav();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = solid || scrolled;
  const textColor = dark ? "text-gaia-charcoal" : "text-gaia-bg";

  const handleLink = (link) => {
    setOpen(false);
    setTimeout(() => {
      if (link.type === "route") navigate(link.to);
      else goToSection(link.target);
    }, 350);
  };

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.2, ease: EASE, delay: solid ? 0.1 : 1.1 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-700 ${
          dark ? "bg-gaia-bg/85 backdrop-blur-md" : "bg-transparent"
        }`}
        data-testid="main-navigation"
      >
        <nav className="relative mx-auto flex min-h-[96px] max-w-[1600px] items-center justify-between px-6 py-5 md:min-h-[116px] md:px-12 md:py-6">
          <button
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            className={`flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] transition-colors duration-700 ${textColor}`}
          >
            <span className="flex flex-col gap-[5px]">
              <span className={`h-px w-6 transition-colors duration-700 ${dark ? "bg-gaia-charcoal" : "bg-gaia-bg"}`} />
              <span className={`h-px w-6 transition-colors duration-700 ${dark ? "bg-gaia-charcoal" : "bg-gaia-bg"}`} />
            </span>
            <span className="hidden sm:inline">Menu</span>
          </button>

          <button
            onClick={() => navigate("/", { state: { scrollTo: "hero" } })}
            data-testid="nav-logo"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            aria-label="Gaia Space home"
          >
            <img
              src="/gaia-logo.png"
              alt="Gaia Space"
              className="h-16 w-auto transition-all duration-700 md:h-[84px]"
              style={dark ? undefined : { filter: "brightness(0) invert(1)", opacity: 0.96 }}
            />
          </button>

          <button
            onClick={() => goToSection("contact")}
            data-testid="nav-contact-btn"
            className={`text-[11px] uppercase tracking-[0.28em] transition-colors duration-700 ${textColor}`}
          >
            Contact
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex flex-col bg-gaia-charcoal text-gaia-bg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            data-testid="nav-overlay"
          >
            <div className="mx-auto flex w-full max-w-[1600px] items-center justify-between px-6 py-5 md:px-12 md:py-6">
              <span className="font-serif text-lg tracking-[0.35em]">GAIA&nbsp;SPACE</span>
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close"
                className="text-[11px] uppercase tracking-[0.28em]"
              >
                Close
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center px-6 md:px-12">
              {NAV_LINKS.map((link, i) => (
                <motion.button
                  key={link.label}
                  onClick={() => handleLink(link)}
                  data-testid={`nav-link-${link.type === "route" ? "gatherings" : link.target}`}
                  className="group flex items-baseline gap-6 border-b border-gaia-bg/15 py-5 text-left"
                  initial={{ opacity: 0, y: 26 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: EASE, delay: 0.12 + i * 0.07 }}
                >
                  <span className="font-sans text-xs tracking-[0.2em] text-gaia-taupe">0{i + 1}</span>
                  <span className="font-serif text-4xl font-light transition-colors duration-500 group-hover:text-gaia-terracotta md:text-6xl">
                    {link.label}
                  </span>
                </motion.button>
              ))}
            </div>
            <div className="mx-auto w-full max-w-[1600px] px-6 pb-10 text-xs tracking-[0.2em] text-gaia-taupe md:px-12">
              hello@gaiaspace.life
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
